import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import ConfirmationDialog from '../components/ui/ConfirmationDialog';
import PasswordField, { MIN_PASSWORD_LENGTH, validateNewPassword } from '../components/auth/PasswordField';
import { ApiService } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';

const AccountSettings = () => {
  const { user, replaceSession, logoutEverywhere } = useAuth();
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    const problem = validateNewPassword(password, confirm);
    if (problem) {
      showError(problem);
      return;
    }
    setSaving(true);
    try {
      const response = await ApiService.changePassword(current, password);
      replaceSession(response.token, response.user);
      setCurrent('');
      setPassword('');
      setConfirm('');
      showSuccess('Password changed. You were signed out on your other devices.');
    } catch (error) {
      showError(error.message || 'Could not change the password.');
    } finally {
      setSaving(false);
    }
  };

  const signOutEverywhere = async () => {
    setSigningOut(true);
    try {
      await logoutEverywhere();
      navigate('/auth', { replace: true });
    } catch (error) {
      showError(error.message || 'Could not sign out everywhere.');
      setSigningOut(false);
      setConfirmSignOut(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-4 lg:p-6 max-w-2xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Account &amp; security</h1>
        <p className="text-sm sm:text-base text-gray-600">{user?.name ? `${user.name} · ` : ''}{user?.email}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Change password</CardTitle>
          <p className="text-sm text-gray-600">You stay signed in here; every other device is signed out.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={changePassword} className="space-y-4">
            <PasswordField id="current-password" label="Current password" value={current} onChange={setCurrent} autoComplete="current-password" />
            <PasswordField
              id="new-password"
              label="New password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
            />
            <PasswordField id="confirm-password" label="Confirm new password" value={confirm} onChange={setConfirm} autoComplete="new-password" />
            <Button type="submit" disabled={saving}>
              <ShieldCheck className="w-4 h-4 mr-2" />
              {saving ? 'Saving…' : 'Change password'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Sign out everywhere</CardTitle>
          <p className="text-sm text-gray-600">
            Ends every session on every device, including this one. Use it if you signed in on a shared computer or think
            someone else has access.
          </p>
        </CardHeader>
        <CardContent>
          <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setConfirmSignOut(true)} disabled={signingOut}>
            <LogOut className="w-4 h-4 mr-2" />
            {signingOut ? 'Signing out…' : 'Sign out on all devices'}
          </Button>
        </CardContent>
      </Card>

      <ConfirmationDialog
        isOpen={confirmSignOut}
        onClose={() => !signingOut && setConfirmSignOut(false)}
        onConfirm={signOutEverywhere}
        title="Sign out on all devices?"
        message="Every session ends, including this one. You'll need to sign in again everywhere."
        confirmText="Sign out everywhere"
        variant="danger"
        isLoading={signingOut}
      />
    </div>
  );
};

export default AccountSettings;
